import { memo, SyntheticEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Button } from 'src/components/Buttons/Button';
import {
  FieldType,
  useForm,
  validateCountryForReg,
  validateEmail,
  validateMaxLength,
  validatePassword,
  validateRequired
} from 'src/hooks/useForm';
import { Trans, useTranslation } from 'react-i18next';
import { MAX_EMAIL_LENGTH, MAX_PASSWORD_LENGTH } from 'src/constants';
import { findCountryCodeByNameForReg, findCountryNameByCodeForReg } from 'src/utils';
import classNames from 'classnames';
import { Tooltip } from 'react-tooltip';
import { Link, useLocation, useSearchParams } from 'react-router';
import project from 'src/config/project.json';
import {
  MESSAGE_STATUS,
  useMessageContext,
  useRegistration,
  useUpdateSubscriptions,
  passAffiliateParams,
  useClickOutside
} from 'react-easyrocket';
import { Input } from 'src/components/Inputs/Input';
import { PasswordInput } from 'src/components/Inputs/PasswordInput';
import { Autocomplete } from 'src/components/Inputs/Autocomplete';
import { Checkbox } from 'src/components/Inputs/Checkbox';
import { AlternativeAuthMethods } from 'src/components/AlternativeAuthMethods';
import { usePasswordChecklist } from 'src/hooks/usePasswordChecklist';
import { useModalContext } from 'src/hooks/useModalContext';
import { useRoute } from 'src/router/hooks';
import { nameRegex } from 'src/constants/regex';
import { SupportedLanguages } from 'src/modules/locales/types';
import { useGetCountryConfig } from 'src/hooks/useGetCountryConfig';
import { ConfigKeys } from 'src/hooks/useGetCountryConfig/types';
import { COUNTRIES_OPTIONS_FOR_REG } from 'src/constants/constants';
import { useCacheSignUpData } from './hooks/useCacheSignUpData';
import { useAuthFlow } from 'src/modules/authFlow/hooks/useAuthFlow';

interface StepOneProps {
  changeStep?: (value: number) => void;
  disableFields?: boolean;
  isCompleted?: boolean;
  onSuccess?: () => void;
}

const _StepOne = ({ changeStep, disableFields = false, onSuccess }: StepOneProps) => {
  const [checkIs18Only, setCheckIs18Only] = useState(false);
  const { mutateAsync: updateSubscription } = useUpdateSubscriptions();
  const { openMessage } = useMessageContext();
  const {
    t,
    i18n: { language }
  } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const emailRef = useRef(null);
  const passwordRef = useRef(null);
  const { ROUTE } = useRoute();
  const { startLogin } = useAuthFlow();
  const { modalProps } = useModalContext<{ onAuthFlowSuccess: () => void }>();
  const { pathname } = useLocation();
  const isLanding = pathname.includes('landing');

  const { config, userCountry, isFetchingUserCountry, isAuthenticated } = useGetCountryConfig({
    language: language as SupportedLanguages
  });
  const getCountryOptions = () => {
    if (!config[ConfigKeys.HideCountryUKDropdown]) {
      return COUNTRIES_OPTIONS_FOR_REG;
    }
    return COUNTRIES_OPTIONS_FOR_REG.filter((country) => {
      return country.id !== 'GBR';
    });
  };

  const { mutateAsync, isLoading } = useRegistration({
    onSuccess: () => {
      const params = new URLSearchParams(searchParams.toString());
      if (params.has('referral')) {
        params.delete('referral');
        setSearchParams(params);
      }
      changeStep?.(1);
      onSuccess?.();
    }
  });

  const {
    values,
    setValues,
    errors,
    fieldsProps: { emailProps, passwordProps, countryProps, is18yearsProps, newsProps },
    isFormValid,
    submitTried,
    setSubmitTried,
    updateDefaultStatePartially,
    focused,
    setFocused
  } = useForm<{
    email: FieldType.Text;
    password: FieldType.Text;
    country: FieldType.Autocomplete;
    is18years: FieldType.Checkbox;
    news: FieldType.Checkbox;
  }>({
    email: {
      placeholder: `${t('general.email')}`,
      fieldType: FieldType.Text,
      validation: [validateRequired, validateMaxLength(MAX_EMAIL_LENGTH), validateEmail]
    },
    password: {
      placeholder: `${t('general.password')}`,
      fieldType: FieldType.Text,
      validation: [validateRequired, validateMaxLength(MAX_PASSWORD_LENGTH), validatePassword]
    },
    country: {
      fieldType: FieldType.Autocomplete,
      placeholder: `${t('general.chooseYourCoutry')}`,
      options: getCountryOptions(),
      validation: [validateRequired, validateCountryForReg]
    },
    is18years: {
      fieldType: FieldType.Checkbox,
      validation: [validateRequired],
      defaultValue: true
    },
    news: {
      fieldType: FieldType.Checkbox,
      defaultValue: true
    }
  });

  const handleSubmit = useCallback(async () => {
    setSubmitTried(true);
    setCheckIs18Only(false);
    if (!isFormValid) return;

    if (
      /@kansspelautoriteit/gm.test(values.email) ||
      /@belastingdienst/gm.test(values.email) ||
      /@rieclimburg/gm.test(values.email)
    )
      return openMessage?.(`${t('registration.regionBlocked')}`, MESSAGE_STATUS.ERROR);

    const referral = searchParams.get('referral') || undefined;
    try {
      await mutateAsync({
        email: values.email,
        password: values.password,
        currency: userCountry?.currency?.length ? userCountry?.currency[0] : undefined,
        country: findCountryCodeByNameForReg(values.country),
        referral,
        ...passAffiliateParams()
      });
      updateSubscription({
        email_bonuses_sub: values.news
      });
    } catch (error) {
      console.error(t('general.error'));
    }
  }, [
    setSubmitTried,
    isFormValid,
    values.email,
    values.password,
    values.country,
    values.news,
    openMessage,
    t,
    searchParams,
    mutateAsync,
    userCountry?.currency,
    updateSubscription
  ]);

  const passwordChecklist = usePasswordChecklist(values.password);

  const checkSubmitSocialLinks = useCallback(
    (e: SyntheticEvent) => {
      if (!values.is18years) {
        e.preventDefault();
        setCheckIs18Only(true);
        setSubmitTried(true);
      }
    },
    [setSubmitTried, values.is18years]
  );

  useEffect(() => {
    if (!userCountry?.country) return;
    updateDefaultStatePartially({
      country: findCountryNameByCodeForReg(userCountry.country)
    });
  }, [userCountry?.country]);

  useClickOutside(emailRef, () => setFocused(''));
  useClickOutside(passwordRef, () => setFocused(''));

  const isDisabledCountryField =
    disableFields || config[ConfigKeys.BlockCountryField] || isFetchingUserCountry;

  useCacheSignUpData({
    triggerForSaveData: focused,
    values,
    setValues,
    storedValues: ['email', 'country']
  });

  return (
    <div className="flex flex-col gap-2">
      <AlternativeAuthMethods checkSubmitSocialLinks={checkSubmitSocialLinks} />

      <form
        className="flex flex-col gap-2 items-center justify-between h-full"
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
        autoComplete="off">
        <div className="flex flex-col gap-2 items-center">
          <div className="w-full relative">
            <Input
              ref={emailRef}
              id="email-input"
              {...emailProps}
              onFocus={() => setFocused('email')}
              disabled={disableFields}
              isError={
                !checkIs18Only && submitTried && focused !== 'email' && Boolean(errors.email)
              }
            />
            <Tooltip
              anchorSelect="#email-input"
              className="!bg-sum_tooltip_bg !text-sum_tooltip_text rounded-4xl z-[1000] !max-w-full leading-4 !py-1.5"
              place="top"
              isOpen={
                !checkIs18Only && focused !== 'email' && submitTried && Boolean(errors.email)
              }>
              {errors.email}
            </Tooltip>
          </div>
          <div className="w-full relative flex items-center" ref={passwordRef}>
            <PasswordInput
              id="password-input"
              {...passwordProps}
              onFocus={() => setFocused('password')}
              disabled={disableFields}
              isError={
                !checkIs18Only && submitTried && focused !== 'password' && Boolean(errors.password)
              }
            />
            <Tooltip
              anchorSelect="#password-input"
              className="!bg-sum_tooltip_bg !text-sum_tooltip_text rounded-4xl z-[1000] !max-w-full leading-4 !py-1.5"
              place="top"
              isOpen={focused === 'password'}>
              <>
                {passwordChecklist.map((error) => (
                  <p key={error.text} className={classNames({ 'line-through': error.done })}>
                    {t(`${error.text}`)}
                  </p>
                ))}
              </>
            </Tooltip>
            <Tooltip
              anchorSelect="#password-input"
              className="!bg-sum_tooltip_bg !text-sum_tooltip_text rounded-4xl z-[1000] !max-w-full leading-4 !py-1.5"
              place="top"
              isOpen={
                !checkIs18Only && submitTried && focused !== 'password' && Boolean(errors.password)
              }>
              {errors.password}
            </Tooltip>
          </div>
          {config[ConfigKeys.HideCountryField] && (
            <div className="w-full relative flex items-center" id="country-autocomplete">
              <Autocomplete
                autocompleteClassName="w-full"
                regexAllowedSymbols={nameRegex}
                {...countryProps}
                disabled={isDisabledCountryField}
                isError={!checkIs18Only && submitTried && Boolean(errors.country)}
                emptyStateMessage={t('autocompleteEmptyState.noSuchCountry') as string}
              />
              <Tooltip
                anchorSelect="#country-autocomplete"
                className="!bg-sum_tooltip_bg !text-sum_tooltip_text rounded-4xl z-[1000] !max-w-full leading-4 !py-1.5"
                place="top"
                isOpen={
                  focused !== 'country' && !checkIs18Only && submitTried && Boolean(errors.country)
                }>
                {errors.country}
              </Tooltip>
            </div>
          )}
          <div className="w-full relative">
            <Checkbox
              id="is18-checkbox"
              disabled={disableFields}
              classNamesForDescription="text-sm font-medium text-sum_text"
              description={
                (
                  <Trans
                    i18nKey="registration.text"
                    components={{
                      1: (
                        <Link
                          target="_blank"
                          to={ROUTE.INFO_PAGES_TERMS.get()}
                          className="font-medium cursor-pointer text-i_checkout_link"
                        />
                      ),
                      2: (
                        <Link
                          target="_blank"
                          to={ROUTE.INFO_PAGES_POLICY.get()}
                          className="font-medium cursor-pointer text-i_checkout_link"
                        />
                      )
                    }}
                  />
                ) as unknown as string
              }
              {...is18yearsProps}
            />
            <Tooltip
              anchorSelect="#is18-checkbox"
              className="!bg-sum_tooltip_bg !text-sum_tooltip_text rounded-4xl z-[20] leading-4 !py-1.5"
              place="bottom"
              isOpen={submitTried && Boolean(errors.is18years)}>
              {errors.is18years}
            </Tooltip>
          </div>
          <Checkbox
            disabled={disableFields}
            classNamesForDescription="text-sm font-medium text-sum_text"
            description={`${t('registration.news', { projectName: project.projectNameUppercase })}`}
            {...newsProps}
          />
        </div>
        <div className="flex flex-col items-center gap-1 w-full">
          <Button
            className="max-w-full"
            title={`${isAuthenticated ? t('registration.nextStep') : t('general.signUp')}`}
            loading={isLoading}
            mode="button-primary"
            type="submit">
            {isAuthenticated ? t('registration.nextStep') : t('general.signUp')}
          </Button>
          {!isLanding && (
            <p className="w-full font-medium text-sm text-sum_text_card m-auto text-center 768:mt-2">
              {t('registration.alreadyHaveAccount')}
              <span
                className="font-thin text-sm underline cursor-pointer text-sum_login pl-1"
                onClick={() =>
                  startLogin({ replace: true, onSuccess: modalProps?.onAuthFlowSuccess })
                }>
                {t('registration.toLogin')}
              </span>
            </p>
          )}
        </div>
      </form>
    </div>
  );
};

export const StepOne = memo(_StepOne);
